import React, { useEffect, useState } from "react";
import { firebaseCache } from "../../utils/firebaseCache";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { addPolicy } from "../policies/policySlice";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase/firebaseConfig";
import { toast } from "react-toastify";
import SuccessDialog from "./SuccessDialog";
import { doc, setDoc, updateDoc, getDoc, collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { v4 as uuidv4 } from "uuid";
import TextField from "@mui/material/TextField";
import { Button, Box, FormControl, InputLabel, Select, MenuItem, Autocomplete, CircularProgress, useMediaQuery } from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { getCompaniesByCategory, getVehicleMakesByType, getPolicyTypesByCategory } from "../../data/dropdowns";
import { resolveBranchName } from "../../utils/branchNameResolver";

const UnifiedPolicyForm = ({ isOpen, onClose, prefillData, pdfFile, mode = "create", existingPolicyId, existingCustomerId, initialCustomerData, onSuccess, requiredFields = [], formTitle = "Enter Policy", agentId }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  // Use passed agentId or fallback to current user's UID
  const effectiveAgentId = agentId || user?.uid;

  // Detect 720p screens for optimization
  const isTinyScreen = useMediaQuery('(max-height:720px)');

  // Reusable TextField styling
  const textFieldStyles = {
    width: "100%",
    "& .MuiInputBase-root": {
      height: isTinyScreen ? "44px" : "56px",
      borderRadius: "8px",
      borderWidth: "1px",
      fontSize: "14px",
      backgroundColor: "#FFFFFF",
      "& fieldset": {
        borderColor: "#E0E0E0",
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: "#BDBDBD",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#0063F7",
        borderWidth: "2px",
      },
      "&.Mui-error fieldset": {
        borderColor: "#d32f2f",
        borderWidth: "2px",
      },
    },
    "& .MuiInputBase-input": {
      padding: isTinyScreen ? "10px 12px" : "16px 12px",
      height: isTinyScreen ? "20px" : "24px",
      display: "flex",
      alignItems: "center",

    },
    "& .MuiInputLabel-root": {
      fontSize: "14px",
      color: "#757575",
      "&.Mui-focused": {
        fontSize: "14px",
        color: "#0063F7",
      },
      "&.Mui-error": {
        color: "#d32f2f",
      },
    },
    "& .MuiFormHelperText-root": {
      margin: "4px 0 0 0",
      fontSize: "12px",
      "&.Mui-error": {
        color: "#d32f2f",
      },
    }
  };

  // Reusable Autocomplete styling
  const autocompleteStyles = {
    width: "100%",
    "& .MuiInputBase-root": {
      height: isTinyScreen ? "44px" : "56px",
      borderRadius: "8px",
      borderWidth: "1px",
      fontSize: "14px",
      backgroundColor: "#FFFFFF",
      "& fieldset": {
        borderColor: "#E0E0E0",
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: "#BDBDBD",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#0063F7",
        borderWidth: "2px",
      },
      "&.Mui-error fieldset": {
        borderColor: "#d32f2f",
        borderWidth: "2px",
      },
    },
    "& .MuiInputBase-input": {
      padding: isTinyScreen ? "10px 12px" : "16px 12px",
      height: isTinyScreen ? "20px" : "24px",
      display: "flex",
      alignItems: "center",
    },
    "& .MuiInputLabel-root": {
      fontSize: "14px",
      color: "#757575",
      "&.Mui-focused": {
        fontSize: "14px",
        color: "#0063F7",
      },
      "&.Mui-error": {
        color: "#d32f2f",
      },
    },
    "& .MuiFormHelperText-root": {
      margin: "4px 0 0 0",
      fontSize: "12px",
      "&.Mui-error": {
        color: "#d32f2f",
      },
    }
  };

  const [category, setCategory] = useState("motor");
  const [subCategory, setSubCategory] = useState("medical");

  const [file, setFile] = useState(pdfFile || null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [finalPdfUrl, setFinalPdfUrl] = useState("");

  const [formData, setFormData] = useState({
    name: initialCustomerData?.name || "",
    mobile: initialCustomerData?.mobile || "",
    email: initialCustomerData?.email || "",
    address: initialCustomerData?.address || "",
    vehicleType: "",
    vehicleNumber: "",
    vehicleMake: "",
    vehicleModel: "",
    policyCompany: "",
    policyType: "",
    policyNumber: "",
    sumInsured: "",
    expiryDate: "",
    tpPremium: "",
    odPremium: "",
    premiumAmount: "",
    netPremium: "",
    discount: "",
    amountPayable: "",
    paidAmount: "",
    modeOfPayment: "",
    remarks: "",
    referredBy: ""
  });

  // Dropdown data states - derive from formData
  const [vehicleTypes] = useState(["Two Wheeler", "Private Vehicle", "Commercial Vehicle"]);
  const [availableMakes, setAvailableMakes] = useState([]);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [availablePolicyTypes, setAvailablePolicyTypes] = useState([]);
  const [paymentModes] = useState(["Cash", "UPI", "UPI+Cash", "Cheque", "Online Transfer", "Online Transfer+Cash"]);
  const [errors, setErrors] = useState({});
  const [showValidationBanner, setShowValidationBanner] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Toggle body class to signal that a modal is open, used to disable sidebar
    if (isOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    // Cleanup on unmount to be safe
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  useEffect(() => {
    // If an existing customer is provided (renewal or add-from-client), fetch from DB and prefill
    const maybePrefillFromDb = async () => {
      try {
        if (existingCustomerId && effectiveAgentId) {
          const customerRef = doc(db, `agents/${effectiveAgentId}/customers/${existingCustomerId}`);
          const snap = await getDoc(customerRef);
          if (snap.exists()) {
            const c = snap.data();
            console.log("✅ Prefilling customer data from existing customer:", c);
            setFormData((prev) => ({
              ...prev,
              name: c.name || "",
              mobile: "",
              email: "",
              address: c.address || "",
              referredBy: c.referredBy || "",
            }));
          }
        } else if (prefillData) {
          // Fall back to prefill from AI if no existing customer
          setFormData((prev) => ({
            ...prev,
            name: prefillData.customer?.name || prev.name,
            mobile: prefillData.customer?.phone || prev.mobile,
            email: prefillData.customer?.email || prev.email,
            address: prefillData.customer?.address || prev.address,
            referredBy: prefillData.referredBy || prev.referredBy || "",
          }));
        }
      } catch (_) {
        // ignore prefill errors
      }
    };
    maybePrefillFromDb();
  }, [existingCustomerId, effectiveAgentId, prefillData]);

  useEffect(() => {
    if (prefillData) {

      // Initialize file for upload
      if (prefillData.rawFile) {
        setFile(prefillData.rawFile);
      }

      let initialCategory = "motor";
      if (prefillData.policyCategory === "medical" || prefillData.policyCategory === "life") {
        initialCategory = prefillData.policyCategory;
      } else if (prefillData.policyCategory === "non-motor") {
        // Map non-motor to health for the radio button
        initialCategory = "health";
      }
      setCategory(initialCategory);

      if (prefillData.policyCategory === "non-motor") {
        setSubCategory(prefillData.policyCategoryType || "medical");
      }

      // Auto-fill vehicle type and make with closest matches
      const extractedVehicleType = prefillData.vehicle?.type || "";
      const extractedVehicleMake = prefillData.vehicle?.make || "";

      const matchedVehicleType = findClosestMatch(extractedVehicleType, vehicleTypes);

      // For vehicle make, we need to wait for the type to be set and makes to be loaded
      const matchedVehicleMake = extractedVehicleMake;

      setFormData((prev) => {
        // Decide whether to preserve existing customer data from DB
        const useExistingCustomerData =
          existingCustomerId &&
          (prev.name || prev.mobile || prev.email || prev.address);

        return {
          ...prev,

          // ---------- CUSTOMER FIELDS ----------
          name: useExistingCustomerData
            ? prev.name
            : (prefillData.customer?.name || prev.name || ""),

          mobile: useExistingCustomerData
            ? prev.mobile
            : (prefillData.customer?.phone || prev.mobile || ""),

          email: useExistingCustomerData
            ? prev.email
            : (prefillData.customer?.email || prev.email || ""),

          address: useExistingCustomerData
            ? prev.address
            : (prefillData.customer?.address || prev.address || ""),

          referredBy: useExistingCustomerData
            ? prev.referredBy
            : (prefillData.referredBy || prev.referredBy || ""),

          // ---------- VEHICLE FIELDS (ALWAYS FROM PDF) ----------
          vehicleType: matchedVehicleType,
          vehicleNumber: prefillData.vehicle?.number || "",
          vehicleMake: "", // populated by next useEffect
          vehicleModel: prefillData.vehicle?.model || "",

          // ---------- POLICY FIELDS (ALWAYS FROM PDF) ----------
          // Note: These will be updated by matching effects once availableLists are populated
          policyNumber: prefillData.policy?.policyNumber || "",
          sumInsured: prefillData.policy?.sumInsured || "",
          expiryDate: prefillData.policy?.renewalDate || "",

          // ---------- PAYMENT ----------
          tpPremium: prefillData.payment?.tpPremium || "",
          odPremium: prefillData.payment?.odPremium || "",
          premiumAmount: prefillData.payment?.totalAmount || "",
          netPremium: (prefillData.policyCategory === "life" || (prefillData.policyCategory === "non-motor" && (prefillData.policyCategoryType === "medical" || prefillData.policyCategoryType === "health")))
            ? (prefillData.payment?.totalAmount || "")
            : (prefillData.payment?.netPremium || ""),
          discount: "0",
          amountPayable: prefillData.payment?.totalAmount || "",
          paidAmount: prefillData.payment?.receivedAmount || ""
        };
      });

    }
  }, [prefillData, vehicleTypes]);

  // Auto-fill vehicle make when type changes
  useEffect(() => {
    if (prefillData && formData.vehicleType) {
      const makes = getVehicleMakesByType(formData.vehicleType);
      const extractedVehicleMake = prefillData.vehicle?.make || "";

      if (extractedVehicleMake && makes.length > 0) {
        const matchedVehicleMake = findClosestMatch(extractedVehicleMake, makes);
        if (matchedVehicleMake && matchedVehicleMake !== formData.vehicleMake) {
          setFormData(prev => ({ ...prev, vehicleMake: matchedVehicleMake }));
        }
      }
    }
  }, [formData.vehicleType, prefillData]);

  // Auto-fill policy company when category changes
  useEffect(() => {
    if (prefillData && category) {
      const companies = getCompaniesByCategory(category);
      const extractedPolicyCompany = prefillData.policy?.company || "";

      console.log("🔍 Auto-fill Policy Company Debug:", {
        extractedPolicyCompany,
        category,
        availableOptions: companies,
        currentPolicyCompany: formData.policyCompany
      });

      if (extractedPolicyCompany && companies.length > 0) {
        const matchedPolicyCompany = findClosestMatch(extractedPolicyCompany, companies);
        console.log("🎯 Policy Company Match Result:", {
          extracted: extractedPolicyCompany,
          matched: matchedPolicyCompany,
          availableOptions: companies
        });

        if (matchedPolicyCompany && matchedPolicyCompany !== formData.policyCompany) {
          console.log("✅ Setting Policy Company:", matchedPolicyCompany);
          setFormData(prev => ({ ...prev, policyCompany: matchedPolicyCompany }));
        }
      }
    }
  }, [category, prefillData]);

  // Auto-fill policy type when category changes
  useEffect(() => {
    if (prefillData && category) {
      const policyTypes = getPolicyTypesByCategory(category);
      const extractedPolicyType = prefillData.policy?.policyType || "";

      console.log("🔍 Auto-fill Policy Type Debug:", {
        extractedPolicyType,
        category,
        availableOptions: policyTypes,
        currentPolicyType: formData.policyType
      });

      if (extractedPolicyType && policyTypes.length > 0) {
        const matchedPolicyType = findClosestMatch(extractedPolicyType, policyTypes);
        console.log("🎯 Policy Type Match Result:", {
          extracted: extractedPolicyType,
          matched: matchedPolicyType,
          availableOptions: policyTypes
        });

        if (matchedPolicyType && matchedPolicyType !== formData.policyType) {
          console.log("✅ Setting Policy Type:", matchedPolicyType);
          setFormData(prev => ({ ...prev, policyType: matchedPolicyType }));
        }
      }
    }
  }, [category, prefillData]);

  // Prefill form data when updating an existing policy
  useEffect(() => {
    const fetchExistingPolicy = async () => {
      if (mode === "update" && existingPolicyId && effectiveAgentId && !prefillData) {
        try {
          const policyRef = doc(db, `agents/${effectiveAgentId}/policies/${existingPolicyId}`);
          const policySnap = await getDoc(policyRef);

          if (policySnap.exists()) {
            const policyData = policySnap.data();

            // Set category - map non-motor to health for display
            if (policyData.policyCategory === "non-motor") {
              setCategory("health");
              setSubCategory(policyData.policyCategoryType || "medical");
            } else {
              setCategory(policyData.policyCategory || "motor");
            }

            // Prefill form data
            // Map vehicle type from database to display format
            const mapVehicleTypeFromDB = (vehicleType) => {
              switch (vehicleType) {
                case "four_wheeler":
                  return "Private Vehicle";
                case "two_wheeler":
                  return "Two Wheeler";
                case "commercial_vehicle":
                  return "Commercial Vehicle";
                default:
                  return vehicleType;
              }
            };

            setFormData((prev) => ({
              ...prev,
              name: policyData.name || "",
              mobile: policyData.mobile || "",
              email: policyData.email || "",
              address: policyData.address || "",
              referredBy: policyData.referredBy || "",
              vehicleType: mapVehicleTypeFromDB(policyData.vehicleType) || "",
              vehicleNumber: policyData.vehicleNumber || "",
              vehicleMake: policyData.vehicleMake || "",
              vehicleModel: policyData.vehicleModel || "",
              policyCompany: policyData.policyCompany || "",
              policyType: policyData.policyType || "",
              policyNumber: policyData.policyNumber || "",
              sumInsured: policyData.sumInsured || "",
              expiryDate: policyData.expiryDate || "",
              tpPremium: policyData.tpPremium || "",
              odPremium: policyData.odPremium || "",
              premiumAmount: policyData.premiumAmount || "",
              amountPayable: policyData.amountPayable || "",
              paidAmount: policyData.paidAmount || "",
              modeOfPayment: policyData.modeOfPayment || "",
              remarks: policyData.remarks || ""
            }));
          }
        } catch (error) {
          console.error("Error fetching existing policy:", error);
        }
      }
    };

    fetchExistingPolicy();
  }, [mode, existingPolicyId, effectiveAgentId, prefillData]);

  // No longer needed - using bundled dropdown data

  // Update available makes when vehicle type changes
  useEffect(() => {
    if (formData.vehicleType) {
      const makes = getVehicleMakesByType(formData.vehicleType);
      setAvailableMakes(makes);
    } else {
      setAvailableMakes([]);
    }
  }, [formData.vehicleType]);

  // Update available companies when policy category changes
  useEffect(() => {
    if (category) {
      const companies = getCompaniesByCategory(category);
      setAvailableCompanies(companies);
    } else {
      setAvailableCompanies([]);
    }
  }, [category]);

  // Update available policy types when policy category changes
  useEffect(() => {
    if (category) {
      const policyTypes = getPolicyTypesByCategory(category);
      setAvailablePolicyTypes(policyTypes);
    } else {
      setAvailablePolicyTypes([]);
    }
  }, [category]);

  // Sync netPremium with premiumAmount for Health and Life categories
  useEffect(() => {
    if (category === "health" || category === "life") {
      setFormData(prev => ({
        ...prev,
        netPremium: prev.premiumAmount
      }));
    }
  }, [category, formData.premiumAmount]);

  // Function to find closest match for auto-fill
  const findClosestMatch = (inputValue, options) => {
    if (!inputValue || !options || options.length === 0) return "";

    const normalizedInput = inputValue.toLowerCase().trim();
    console.log('🔍 Matching company:', { input: normalizedInput, options });

    // 1. Try exact match (case insensitive)
    const exactMatch = options.find(option =>
      option.toLowerCase().trim() === normalizedInput
    );
    if (exactMatch) {
      console.log('✅ Exact match:', exactMatch);
      return exactMatch;
    }

    // 2. Try removing common suffixes and match base name
    const commonSuffixes = [
      'general insurance', 'insurance', 'assurance', 'ltd', 'limited', 'company', 'co.', 'pvt', 'private', 'ltd.'
    ];

    // Clean the input by removing common suffixes and extra spaces
    let cleanedInput = normalizedInput;
    commonSuffixes.forEach(suffix => {
      cleanedInput = cleanedInput.replace(new RegExp(`\\s*${suffix}\\s*`, 'g'), ' ');
    });
    cleanedInput = cleanedInput.replace(/\s+/g, ' ').trim();

    // Try matching with cleaned input
    if (cleanedInput !== normalizedInput) {
      const cleanedMatch = options.find(option => {
        const optionLower = option.toLowerCase().trim();
        return optionLower === cleanedInput ||
          optionLower.startsWith(cleanedInput) ||
          optionLower.includes(cleanedInput);
      });

      if (cleanedMatch) {
        console.log('✅ Cleaned match:', cleanedMatch);
        return cleanedMatch;
      }
    }

    // 3. Try matching first two words (for cases like 'Go Digit General Insurance' -> 'Go Digit')
    const words = cleanedInput.split(' ');
    if (words.length > 2) {
      const firstTwoWords = words.slice(0, 2).join(' ');
      const firstTwoMatch = options.find(option =>
        option.toLowerCase().includes(firstTwoWords)
      );

      if (firstTwoMatch) {
        console.log('✅ First two words match:', firstTwoMatch);
        return firstTwoMatch;
      }
    }

    // 4. Try partial match (either way)
    // Filter out common short words to avoid false positives (like 'and' matching 'Cholamandalam')
    const stopWords = ['and', 'the', 'for', 'with', 'ltd', 'pvt', 'co'];
    const significantWords = cleanedInput.split(' ').filter(word =>
      word.length > 3 && !stopWords.includes(word)
    );

    const partialMatch = options.find(option => {
      const optionLower = option.toLowerCase();
      return (
        optionLower.includes(cleanedInput) ||
        cleanedInput.includes(optionLower) ||
        significantWords.some(word => optionLower.includes(word)) ||
        optionLower.split(' ').some(word => word.length > 3 && cleanedInput.includes(word))
      );
    });

    if (partialMatch) {
      console.log('✅ Partial match:', partialMatch);
      return partialMatch;
    }

    // 5. Try first word match as last resort
    const firstWord = cleanedInput.split(' ')[0];
    if (firstWord && firstWord.length > 3) {
      const firstWordMatch = options.find(option =>
        option.toLowerCase().startsWith(firstWord)
      );

      if (firstWordMatch) {
        console.log('✅ First word match:', firstWordMatch);
        return firstWordMatch;
      }
    }

    console.log('❌ No suitable match found');
    return "";
  };

  // Initialize dropdowns on component mount
  useEffect(() => {
    if (isOpen) {
      // Initialize dropdowns with current category/vehicle type
      if (category) {
        setAvailableCompanies(getCompaniesByCategory(category));
        setAvailablePolicyTypes(getPolicyTypesByCategory(category));
      }
      if (formData.vehicleType) {
        setAvailableMakes(getVehicleMakesByType(formData.vehicleType));
      }
    }
  }, [isOpen, category, formData.vehicleType]);

  const validateReferredBy = (name) => {
    if (!name) return ""; // Optional field
    if (!/^[a-zA-Z\s]+$/.test(name)) return "Name should contain only alphabets";
    return "";
  };

  // Validation functions
  const validateName = (name) => {
    if (!name) return "Name is required";
    if (!/^[a-zA-Z\s]+$/.test(name)) return "Name should contain only alphabets";
    if (name.length < 2) return "Name should be at least 2 characters";
    if (name.length > 50) return "Name should not exceed 50 characters";
    return "";
  };

  const validateMobile = (mobile) => {
    if (!mobile) return "Mobile number is required";
    if (!/^\d+$/.test(mobile)) return "Mobile should contain only digits";
    if (mobile.length !== 10) return "Mobile should be exactly 10 digits";
    return "";
  };

  const validateEmail = (email) => {
    if (!email) return ""; // Email is optional
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Please enter a valid email address";
    return "";
  };

  const validateDate = (date) => {
    if (!date) return "Date is required";

    // Strict DD-MM-YYYY format validation
    const dateRegex = /^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-\d{4}$/;
    if (!dateRegex.test(date)) return "Date should be in DD-MM-YYYY format";

    // Parse using DD-MM-YYYY format to avoid MM-DD confusion
    const [day, month, year] = date.split('-');
    const inputDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    const today = new Date();

    // Validate the parsed date is valid
    if (isNaN(inputDate.getTime())) return "Invalid date";

    if (inputDate < today) return "Date cannot be in the past";
    return "";
  };

  const validatePaidAmount = (amount) => {
    if (!amount) return "Amount received is required";
    if (!/^\d+(\.\d{1,2})?$/.test(amount)) return "Please enter a valid amount";
    if (parseFloat(amount) < 0) return "Amount cannot be negative";

    // Check if paid amount exceeds premium amount
    const premiumAmount = parseFloat(formData.premiumAmount) || 0;
    if (parseFloat(amount) > premiumAmount) return `Amount cannot exceed premium amount (₹${premiumAmount})`;

    return "";
  };

  const validateAmount = (amount) => {
    if (!amount) return "Amount is required";
    if (!/^\d+(\.\d{1,2})?$/.test(amount)) return "Amount should be a valid number";
    if (parseFloat(amount) <= 0) return "Amount should be greater than 0";
    return "";
  };

  const validateDiscount = (amount) => {
    if (!amount || amount === "0") return ""; // Allow empty or 0
    if (!/^\d+(\.\d{1,2})?$/.test(amount)) return "Amount should be a valid number";
    if (parseFloat(amount) < 0) return "Amount cannot be negative";
    return "";
  };

  const validateVehicleNumber = (number) => {
    if (!number) return "Vehicle number is required";
    return "";
  };

  const validateMake = (make) => {
    if (!make) return "Make is required";
    return "";
  };

  const validateVehicleType = (type) => {
    if (!type) return "Vehicle type is required";
    return "";
  };

  const validateVehicleModel = (model) => {
    if (!model) return "Vehicle model is required";
    return "";
  };

  const validatePolicyNumber = (number) => {
    if (!number) return "Policy number is required";
    return "";
  };

  const validatePolicyType = (type) => {
    if (!type) return "Policy type is required";
    return "";
  };

  const validatePolicyCompany = (company) => {
    if (!company) return "Policy company is required";
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Apply input restrictions based on field type
    switch (name) {
      case 'mobile':
        // Only allow digits for mobile
        processedValue = value.replace(/[^0-9]/g, '');
        break;
      case 'premiumAmount':
      case 'paidAmount':
      case 'sumInsured':
      case 'netPremium':
      case 'tpPremium':
      case 'odPremium':
      case 'discount':
      case 'amountPayable':
        // Only allow digits and decimal point for amounts
        processedValue = value.replace(/[^0-9.]/g, '');
        // Prevent multiple decimal points
        const decimalCount = (processedValue.match(/\./g) || []).length;
        if (decimalCount > 1) {
          const parts = processedValue.split('.');
          processedValue = parts[0] + '.' + parts.slice(1).join('');
        }
        // Limit to 2 decimal places
        if (processedValue.includes('.')) {
          const parts = processedValue.split('.');
          if (parts[1] && parts[1].length > 2) {
            processedValue = parts[0] + '.' + parts[1].substring(0, 2);
          }
        }
        break;
      case 'name':
      case 'policyType':
        // Only allow letters and spaces, auto-capitalize
        processedValue = value.replace(/[^a-zA-Z\s]/g, '').toUpperCase();
        break;
      case 'vehicleNumber':
        // Allow letters, numbers, and hyphens, auto-capitalize
        processedValue = value.replace(/[^a-zA-Z0-9-]/g, '').toUpperCase();
        break;
      case 'policyNumber':
        // Allow alphanumeric and common symbols, auto-capitalize
        processedValue = value.replace(/[^a-zA-Z0-9\-_\/]/g, '').toUpperCase();
        break;
      case 'expiryDate':
        // Only allow digits and hyphens for DD-MM-YYYY format
        let cleaned = value.replace(/[^0-9]/g, '');

        // Auto-add hyphens after DD and MM
        if (cleaned.length >= 2) {
          processedValue = cleaned.substring(0, 2);
          if (cleaned.length >= 3) {
            processedValue += '-' + cleaned.substring(2, 4);
          }
          if (cleaned.length >= 5) {
            processedValue += '-' + cleaned.substring(4, 8);
          }
        } else {
          processedValue = cleaned;
        }
        break;
      case 'email':
        // Allow email characters, keep lowercase
        processedValue = value.toLowerCase();
        break;
      case 'address':
        // Allow letters, numbers, spaces, and common punctuation, auto-capitalize
        processedValue = value.replace(/[^a-zA-Z0-9\s,.-]/g, '').toUpperCase();
        break;
      case 'remarks':
        // Allow alphanumeric and special characters, keep original case
        processedValue = value; // No transformation needed for remarks
        break;
      case 'referredBy':
        // Same as name: Only allow letters and spaces, auto-capitalize
        processedValue = value.replace(/[^a-zA-Z\s]/g, '').toUpperCase();
        break;
      default:
        // For other fields, auto-capitalize alphanumeric content
        if (value.match(/[a-zA-Z]/)) {
          processedValue = value.toUpperCase();
        }
    }

    setFormData(prev => {
      const newData = { ...prev, [name]: processedValue };

      // Auto-calculate Amount Payable: Total Premium - Customer Discount
      if (name === 'premiumAmount' || name === 'discount') {
        const total = parseFloat(newData.premiumAmount) || 0;
        const disc = parseFloat(newData.discount) || 0;
        newData.amountPayable = (total - disc).toString();
      }

      return newData;
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Case 1: New Lead (ClientList) - Only name, mobile, expiry date mandatory
    // Check if requiredFields is specifically for New Lead scenario
    if (requiredFields.length > 0 && requiredFields.includes('name') && requiredFields.includes('mobile') && requiredFields.includes('expiryDate')) {
      requiredFields.forEach(field => {
        switch (field) {
          case 'name':
            newErrors.name = validateName(formData.name);
            break;
          case 'mobile':
            newErrors.mobile = validateMobile(formData.mobile);
            break;
          case 'expiryDate':
            newErrors.expiryDate = validateDate(formData.expiryDate);
            break;
          default:
            break;
        }
      });

      setErrors(newErrors);
      return !Object.values(newErrors).some(error => error !== "");
    }

    // Case 2: Upload Policy (prefillData exists) - ALL fields mandatory
    if (prefillData) {
      newErrors.name = validateName(formData.name);
      newErrors.mobile = validateMobile(formData.mobile);
      newErrors.email = validateEmail(formData.email);
      newErrors.referredBy = validateReferredBy(formData.referredBy);

      // Validate vehicle fields (only for motor category)
      if (category === "motor") {
        newErrors.vehicleType = validateVehicleType(formData.vehicleType);
        newErrors.vehicleNumber = validateVehicleNumber(formData.vehicleNumber);
        newErrors.vehicleMake = validateMake(formData.vehicleMake);
        newErrors.vehicleModel = validateVehicleModel(formData.vehicleModel);
      }

      // Validate policy fields
      newErrors.expiryDate = validateDate(formData.expiryDate);
      newErrors.premiumAmount = validateAmount(formData.premiumAmount);
      newErrors.paidAmount = validatePaidAmount(formData.paidAmount);
      if (formData.netPremium) newErrors.netPremium = validateAmount(formData.netPremium);
      newErrors.discount = validateDiscount(formData.discount);
      newErrors.policyNumber = validatePolicyNumber(formData.policyNumber);
      newErrors.policyType = validatePolicyType(formData.policyType);
      newErrors.policyCompany = validatePolicyCompany(formData.policyCompany);

      setErrors(newErrors);
      return !Object.values(newErrors).some(error => error !== "");
    }

    // Case 3: Enter Policy (Renewals) - ALL fields mandatory (existing behavior preserved)
    newErrors.name = validateName(formData.name);
    newErrors.mobile = validateMobile(formData.mobile);
    newErrors.email = validateEmail(formData.email);
    newErrors.referredBy = validateReferredBy(formData.referredBy);

    // Validate vehicle fields (only for motor category)
    if (category === "motor") {
      newErrors.vehicleType = validateVehicleType(formData.vehicleType);
      newErrors.vehicleNumber = validateVehicleNumber(formData.vehicleNumber);
      newErrors.vehicleMake = validateMake(formData.vehicleMake);
      newErrors.vehicleModel = validateVehicleModel(formData.vehicleModel);
    }

    // Validate policy fields
    newErrors.expiryDate = validateDate(formData.expiryDate);
    newErrors.premiumAmount = validateAmount(formData.premiumAmount);
    newErrors.paidAmount = validatePaidAmount(formData.paidAmount);
    if (formData.netPremium) newErrors.netPremium = validateAmount(formData.netPremium);
    newErrors.discount = validateDiscount(formData.discount);
    newErrors.policyNumber = validatePolicyNumber(formData.policyNumber);
    newErrors.policyType = validatePolicyType(formData.policyType);
    newErrors.policyCompany = validatePolicyCompany(formData.policyCompany);

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== "");
  };

  const handleSubmit = async () => {
    if (isSubmitting) return; // Prevent multiple submissions
    // Validate form before submission
    if (!validateForm()) {
      setShowValidationBanner(true);
      // Hide banner after 3 seconds
      setTimeout(() => setShowValidationBanner(false), 3000);
      return;
    }

    try {
      setIsSubmitting(true);
      let pdfUrl = finalPdfUrl || (prefillData?.pdfUrl ?? "");
      let pdfSource = "firebase"; // Default to firebase for backward compatibility
      let driveFileId = null;
      let firebaseUrl = null;
      const fileToUpload = file || pdfFile; // Use local state if available, fallback to prop

      if (!pdfUrl && fileToUpload) {
        try {
          // Check if Google Drive is linked for this user/admin
          const { checkDriveStatus, uploadToDrive } = await import("../../utils/driveApi");
          const driveStatus = await checkDriveStatus();

          // Compress PDF before uploading (works for both Drive and Firebase)
          let uploadFile = fileToUpload;
          try {
            const compressionToast = toast.info("Optimizing PDF...", { autoClose: false, isLoading: true });
            const { compressPdf } = await import("../../utils/pdfCompressor");
            uploadFile = await compressPdf(fileToUpload);
            toast.dismiss(compressionToast);
            if (uploadFile.size < fileToUpload.size) {
              toast.success(`PDF Optimized: ${((fileToUpload.size - uploadFile.size) / 1024).toFixed(0)}KB saved`, { autoClose: 2000 });
            }
          } catch (compError) {
            console.error("PDF Compression failed, proceeding with original file:", compError);
          }

          if (driveStatus.linked) {
            // Upload to Google Drive
            console.log("📁 Uploading to Google Drive...");
            const uploadToast = toast.info("Uploading to Google Drive...", { autoClose: false, isLoading: true });

            try {
              // Determine branch name using the resolver
              const isAdmin = user?.isPhoneUser || user?.providerData?.some(p => p.providerId === 'phone');
              const branchName = await resolveBranchName(user, effectiveAgentId, isAdmin);

              if (!branchName && !isAdmin) {
                toast.dismiss(uploadToast);
                toast.error("Please add a Branch Name in your profile before uploading to Google Drive.", { autoClose: 5000 });
                // Fallback to Firebase Storage if Drive branch name is missing
                throw new Error("Missing branch name for Drive upload");
              }

              const clientName = formData.name || 'Unknown';
              const referredBy = formData.referredBy || '';
              const clientFolderName = referredBy ? `${clientName} (Ref: ${referredBy})` : clientName;
              const policyNumber = formData.policyNumber || `POLICY_${Date.now()}`;

              const driveResult = await uploadToDrive(uploadFile, branchName, clientFolderName, policyNumber);
              driveFileId = driveResult.fileId;
              pdfUrl = driveResult.webViewLink || `https://drive.google.com/file/d/${driveFileId}/view`;
              pdfSource = "drive";

              toast.dismiss(uploadToast);
              toast.success("PDF uploaded to Google Drive", { autoClose: 2000 });
              console.log("✅ PDF uploaded to Drive:", driveFileId);
            } catch (driveError) {
              toast.dismiss(uploadToast);
              console.error("❌ Drive upload failed, falling back to Firebase:", driveError);
              toast.warning("Drive upload failed, using Firebase Storage", { autoClose: 2000 });

              // Fallback to Firebase Storage
              const storageRef = ref(storage, `policies/${effectiveAgentId}/${Date.now()}_${uploadFile.name}`);
              const snapshot = await uploadBytes(storageRef, uploadFile);
              pdfUrl = await getDownloadURL(snapshot.ref);
              firebaseUrl = pdfUrl;
              pdfSource = "firebase";
              console.log("✅ Fallback: PDF uploaded to Firebase Storage");
            }
          } else {
            // Upload to Firebase Storage (Drive not linked)
            console.log("📦 Uploading to Firebase Storage (Drive not linked)...");
            const storageRef = ref(storage, `policies/${effectiveAgentId}/${Date.now()}_${uploadFile.name}`);
            const snapshot = await uploadBytes(storageRef, uploadFile);
            pdfUrl = await getDownloadURL(snapshot.ref);
            firebaseUrl = pdfUrl;
            pdfSource = "firebase";
            console.log("✅ PDF uploaded to Firebase Storage");
          }

          setFinalPdfUrl(pdfUrl);
        } catch (uploadError) {
          console.error("❌ PDF upload failed during submission:", uploadError);
          toast.error("Failed to upload policy PDF.");
          return;
        }
      } else if (pdfUrl) {
        // Existing policy with PDF URL - determine source from URL pattern
        if (pdfUrl.includes('drive.google.com')) {
          pdfSource = "drive";
          // Try to extract fileId from URL
          const driveMatch = pdfUrl.match(/\/d\/([a-zA-Z0-9_-]+)/);
          if (driveMatch) driveFileId = driveMatch[1];
        } else {
          pdfSource = "firebase";
          firebaseUrl = pdfUrl;
        }
      }

      // Only show error if user is in create mode and using PDF upload flow (pdfFile is present)
      if (!pdfUrl && pdfFile) {
        toast.error("PDF not uploaded yet. Please upload and extract first.");
        return;
      }

      const policyId = mode === "update" && existingPolicyId ? existingPolicyId : uuidv4();

      // Check if customer already exists by name and mobile combination
      let customerId = existingCustomerId;
      let isNewCustomer = false;

      if (!customerId) {
        try {
          const customersRef = collection(db, `agents/${effectiveAgentId}/customers`);
          const q = query(
            customersRef,
            where("name", "==", formData.name),
            where("mobile", "==", formData.mobile)
          );
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            // Customer exists, use their ID
            customerId = querySnapshot.docs[0].id;
            console.log("✅ Found existing customer:", customerId);
          } else {
            // Customer doesn't exist, create new one
            customerId = uuidv4();
            isNewCustomer = true;
            console.log("✅ Creating new customer:", customerId);
          }
        } catch (error) {
          console.error("❌ Error checking for existing customer:", error);
          // Fallback to creating new customer
          customerId = uuidv4();
          isNewCustomer = true;
          console.log("✅ Creating new customer:", customerId);
        }
      }

      const customerPath = `agents/${effectiveAgentId}/customers/${customerId}`;

      const customerPayload = {
        name: formData.name,
        mobile: formData.mobile,
        email: formData.email,
        address: formData.address,
        referredBy: formData.referredBy || "",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        agentId: effectiveAgentId,
        // Add status field for new leads
        status: (requiredFields.length > 0 && requiredFields.includes('name') && requiredFields.includes('mobile') && requiredFields.includes('expiryDate')) ? "New Lead" : "Customer"
      };

      // If we are updating or reusing an existing customer, merge to avoid clobbering fields
      if (mode === "update" || existingCustomerId || !isNewCustomer) {
        await setDoc(doc(db, customerPath), customerPayload, { merge: true });
      } else {
        await setDoc(doc(db, customerPath), customerPayload);

        // Note: Cache invalidation is handled by the parent component's onSuccess callback
        // based on the write operation context (renewals_write, clientlist_write, etc.)
        console.log("✅ New customer created:", customerId);
      }

      // Check if policy number already exists for this customer
      if (mode !== "update" && formData.policyNumber) {
        try {
          const policiesRef = collection(db, `agents/${effectiveAgentId}/policies`);
          const policyQuery = query(
            policiesRef,
            where("customerId", "==", customerId),
            where("policyNumber", "==", formData.policyNumber)
          );
          const policyQuerySnapshot = await getDocs(policyQuery);

          if (!policyQuerySnapshot.empty) {
            // Policy already exists for this customer
            toast.error("Policy already exists for this customer", {
              containerId: "bottom-center",
              position: "bottom-center",
              autoClose: 3000,
              hideProgressBar: true,
              closeOnClick: false,
              pauseOnHover: false,
              draggable: false,
              closeButton: false,
              style: {
                backgroundColor: '#FFADA8',
                color: '#DC2626',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 500,
                padding: '12px 16px',
                minHeight: '44px',
                alignItems: 'center',
                gap: '2px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                marginBottom: '75vh',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: 'auto',
                maxWidth: 'none',
                display: 'inline-flex',
              },
              icon: (
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: '#DC2626',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '8px',
                  flexShrink: 0
                }}>
                  <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>!</span>
                </div>
              )
            });
            return;
          }
        } catch (error) {
          console.error("❌ Error checking for existing policy:", error);
          // Continue with policy creation if check fails
        }
      }

      const docPath = `agents/${effectiveAgentId}/policies/${policyId}`;

      let policyCategoryType = "vehicle";
      let finalPolicyCategory = category;

      if (category === "health") {
        // Map health to non-motor for storage
        finalPolicyCategory = "non-motor";
        policyCategoryType = subCategory;
      } else if (category === "non-motor") {
        policyCategoryType = subCategory;
      } else if (category === "life") {
        policyCategoryType = "life";
      }

      // Map vehicle type for database storage
      const mapVehicleTypeForDB = (vehicleType) => {
        switch (vehicleType) {
          case "Private Vehicle":
            return "four_wheeler";
          case "Two Wheeler":
            return "two_wheeler";
          case "Commercial Vehicle":
            return "commercial_vehicle";
          default:
            return vehicleType;
        }
      };

      const policyPayload = {
        ...formData,
        totalAmount: formData.premiumAmount,
        receivedAmount: formData.paidAmount,
        vehicleType: mapVehicleTypeForDB(formData.vehicleType),
        amountPayable: formData.amountPayable || formData.premiumAmount,
        agentId: effectiveAgentId,
        referredBy: formData.referredBy || "", // Moved to direct field
        policyCategory: finalPolicyCategory,
        policyCategoryType,
        customerId,
        pdfUrl,
        // Google Drive integration fields
        pdfSource: pdfSource,
        driveFileId: driveFileId || null,
        firebaseUrl: firebaseUrl || (pdfSource === "firebase" ? pdfUrl : null),
        createdAt: new Date().toISOString(),
        // Set status based on context - if it's a New Lead from ClientList, set as "New Lead"
        status: (requiredFields.length > 0 && requiredFields.includes('name') && requiredFields.includes('mobile') && requiredFields.includes('expiryDate')) ? "New Lead" : "Active"
      };

      if (mode === "update") {
        await setDoc(doc(db, docPath), policyPayload, { merge: true });
      } else {
        await setDoc(doc(db, docPath), policyPayload);
      }

      // Invalidate cache immediately after write to ensure UI shows fresh data
      firebaseCache.invalidateAfterWrite('renewals_write', effectiveAgentId);

      setFinalPdfUrl(pdfUrl);
      // Prefer parent-controlled success dialog if provided
      if (onSuccess) {
        onSuccess(pdfUrl, formData);
      } else {
        setShowSuccess(true);
      }
      onClose();
      setFormData({
        name: "",
        mobile: "",
        email: "",
        address: "",
        referredBy: "",
        vehicleType: "",
        vehicleNumber: "",
        vehicleMake: "",
        vehicleModel: "",
        policyCompany: "",
        policyType: "",
        policyNumber: "",
        sumInsured: "",
        expiryDate: "",
        premiumAmount: "",
        netPremium: "",
        discount: "",
        paidAmount: "",
        modeOfPayment: "",
        remarks: ""
      });
      setFile(null);
    } catch (err) {
      console.error("Failed to upload policy:", err);
      toast.error("Error saving policy.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Only hide if both form and success are not shown
  if (!isOpen && !showSuccess) {
    return null;
  }

  return (
    <>
      {isOpen && (
        <div className="unified-modal-overlay">
          <div
            className="unified-modal-content"
            style={{
              display: 'flex',
              flexDirection: 'column',
              maxWidth: '1037px',
              width: '100%',
              height: '840px',
              borderRadius: '12px',
              background: '#FFFFFF',
              padding: '20px 32px',
              gap: '32px',
              position: 'relative'
            }}
          >
            {/* Close button moved into header below */}
            {/* Header */}
            <div
              style={{
                marginTop: '16px',
                marginBottom: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                maxWidth: '973px',
                width: '100%',
                height: '28px',
                flexShrink: 0,
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontFamily: "Roboto Slab",
                  fontWeight: 500,
                  fontSize: "32px",
                  lineHeight: "56px",
                  letterSpacing: "0.02em",
                  color: "#212121",
                }}
              >
                {formTitle}
              </h2>
              <IconButton
                onClick={onClose}
                size="small"
                sx={{
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </div>

            {/* Category Radios */}
            <div
              style={{
                marginTop: '8px',
                marginBottom: '16px',
                display: 'flex',
                gap: '32px',
                alignItems: 'center',
                margin: '0 auto',
                maxWidth: '973px',
                width: '100%',
                height: '28px',
                flexShrink: 0,
              }}
            >
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="policyCategory"
                  value="motor"
                  checked={category === "motor"}
                  onChange={() => {
                    setCategory("motor");
                    setFormData(prev => ({ ...prev, policyCompany: "", policyType: "" }));
                  }}
                  style={{ accentColor: '#007bff', width: '18px', height: '18px' }}
                />
                <span
                  style={{
                    fontFamily: 'Roboto',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '20px',
                    letterSpacing: '1%',
                    color: category === "motor" ? '#0063F7' : '#000000',
                  }}
                >
                  Motor Insurance
                </span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="policyCategory"
                  value="life"
                  checked={category === "life"}
                  onChange={() => {
                    setCategory("life");
                    setFormData(prev => ({ ...prev, policyCompany: "", policyType: "" }));
                  }}
                  style={{ accentColor: '#007bff', width: '18px', height: '18px' }}
                />
                <span
                  style={{
                    fontFamily: 'Roboto',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '20px',
                    letterSpacing: '1%',
                    color: category === "life" ? '#0063F7' : '#000000',
                  }}
                >
                  Life Insurance
                </span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="policyCategory"
                  value="health"
                  checked={category === "health"}
                  onChange={() => {
                    setCategory("health");
                    setFormData(prev => ({ ...prev, policyCompany: "", policyType: "" }));
                  }}
                  style={{ accentColor: '#007bff', width: '18px', height: '18px' }}
                />
                <span
                  style={{
                    fontFamily: 'Roboto',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '20px',
                    letterSpacing: '1%',
                    color: category === "health" ? '#0063F7' : '#000000',
                  }}
                >
                  Non Motor Insurance
                </span>
              </label>
            </div>

            {/* Scrollable Content */}
            <div
              style={{
                maxWidth: '973px',
                width: '100%',
                height: '548px',
                padding: '8px',
                gap: '10px',
                overflowY: 'auto',
                overflowX: 'hidden',
                flexGrow: 1,
              }}
            >
              <form>
                <h3
                  style={{
                    fontFamily: 'Roboto Slab',
                    fontWeight: 500,
                    fontSize: isTinyScreen ? '16px' : '20px',
                    lineHeight: isTinyScreen ? '24px' : '32px',
                    letterSpacing: '1%',
                    color: '#212121',
                    marginBottom: isTinyScreen ? '12px' : '16px'
                  }}
                >
                  Customer Details
                </h3>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: isTinyScreen ? '12px' : '20px', mb: isTinyScreen ? '16px' : '24px' }}>
                  <TextField
                    variant="outlined"
                    label="Mobile"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    error={!!errors.mobile}
                    helperText={errors.mobile}
                    inputProps={{
                      maxLength: 10,
                      inputMode: "numeric",
                      pattern: "[0-9]*"
                    }}
                    sx={textFieldStyles}
                  />
                  <TextField
                    variant="outlined"
                    label="Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    error={!!errors.name}
                    helperText={errors.name}
                    inputProps={{
                      maxLength: 50,
                      autoCapitalize: "words"
                    }}
                    sx={textFieldStyles}
                  />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: isTinyScreen ? '12px' : '20px', mb: isTinyScreen ? '16px' : '24px' }}>
                  <TextField
                    variant="outlined"
                    label="E-mail"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!errors.email}
                    helperText={errors.email}
                    inputProps={{
                      maxLength: 100,
                      inputMode: "email",
                      autoCapitalize: "none"
                    }}
                    sx={textFieldStyles}
                  />
                  <TextField
                    variant="outlined"
                    label="Referred by"
                    name="referredBy"
                    value={formData.referredBy || ""}
                    onChange={handleChange}
                    error={!!errors.referredBy}
                    helperText={errors.referredBy}
                    inputProps={{
                      maxLength: 50,
                      autoCapitalize: "words"
                    }}
                    sx={textFieldStyles}
                  />
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: isTinyScreen ? '12px' : '20px', mb: isTinyScreen ? '16px' : '24px' }}>
                  <TextField
                    variant="outlined"
                    label="Address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    inputProps={{
                      maxLength: 200,
                      autoCapitalize: "words"
                    }}
                    sx={textFieldStyles}
                  />
                </Box>

                {category === "motor" && (
                  <>
                    <h3
                      style={{
                        fontFamily: 'Roboto Slab',
                        fontWeight: 500,
                        fontSize: '20px',
                        lineHeight: '32px',
                        letterSpacing: '1%',
                        color: '#212121',
                        marginBottom: '16px'
                      }}
                    >
                      Vehicle Details
                    </h3>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: isTinyScreen ? '12px' : '20px', mb: isTinyScreen ? '16px' : '24px' }}>
                      <Autocomplete
                        options={vehicleTypes}
                        value={formData.vehicleType}
                        onChange={(event, newValue) => {
                          setFormData(prev => ({ ...prev, vehicleType: newValue || "", vehicleMake: "" }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Type"
                            variant="outlined"
                            sx={autocompleteStyles}
                            error={!!errors.vehicleType}
                            helperText={errors.vehicleType}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <KeyboardArrowDownIcon
                                  sx={{
                                    color: '#757575',
                                    position: 'absolute',
                                    right: '12px',
                                    pointerEvents: 'none'
                                  }}
                                />
                              ),
                            }}
                          />
                        )}
                        freeSolo
                        selectOnFocus
                        clearOnBlur={false}
                        handleHomeEndKeys
                        disableClearable
                      />
                      <TextField
                        variant="outlined"
                        label="Registration No"
                        name="vehicleNumber"
                        value={formData.vehicleNumber}
                        onChange={handleChange}
                        error={!!errors.vehicleNumber}
                        helperText={errors.vehicleNumber}
                        inputProps={{
                          maxLength: 20,
                          autoCapitalize: "characters"
                        }}
                        sx={textFieldStyles}
                      />
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: isTinyScreen ? '12px' : '20px', mb: isTinyScreen ? '16px' : '24px' }}>
                      <Autocomplete
                        options={availableMakes}
                        value={formData.vehicleMake}
                        onChange={(event, newValue) => {
                          setFormData(prev => ({ ...prev, vehicleMake: newValue || "" }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Make"
                            variant="outlined"
                            sx={autocompleteStyles}
                            disabled={!formData.vehicleType}
                            error={!!errors.vehicleMake}
                            helperText={errors.vehicleMake}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <KeyboardArrowDownIcon
                                  sx={{
                                    color: '#757575',
                                    position: 'absolute',
                                    right: '12px',
                                    pointerEvents: 'none'
                                  }}
                                />
                              ),
                            }}
                          />
                        )}
                        freeSolo
                        selectOnFocus
                        clearOnBlur={false}
                        handleHomeEndKeys
                        disabled={!formData.vehicleType}
                        disableClearable
                      />
                      <TextField
                        variant="outlined"
                        label="Model"
                        name="vehicleModel"
                        value={formData.vehicleModel}
                        onChange={handleChange}
                        error={!!errors.vehicleModel}
                        helperText={errors.vehicleModel}
                        inputProps={{
                          maxLength: 50,
                          autoCapitalize: "words"
                        }}
                        sx={textFieldStyles}
                      />
                    </Box>
                  </>
                )}

                <h3
                  style={{
                    fontFamily: 'Roboto Slab',
                    fontWeight: 500,
                    fontSize: isTinyScreen ? '16px' : '20px',
                    lineHeight: isTinyScreen ? '24px' : '32px',
                    letterSpacing: '1%',
                    color: '#212121',
                    marginBottom: isTinyScreen ? '12px' : '16px'
                  }}
                >
                  Policy Details
                </h3>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: isTinyScreen ? '12px' : '20px', mb: isTinyScreen ? '16px' : '24px' }}>
                  <Autocomplete
                    options={availableCompanies}
                    value={formData.policyCompany}
                    onChange={(event, newValue) => {
                      setFormData(prev => ({ ...prev, policyCompany: newValue || "" }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Company"
                        variant="outlined"
                        sx={autocompleteStyles}
                        error={!!errors.policyCompany}
                        helperText={errors.policyCompany}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <KeyboardArrowDownIcon
                              sx={{
                                color: '#757575',
                                position: 'absolute',
                                right: '12px',
                                pointerEvents: 'none'
                              }}
                            />
                          ),
                        }}
                      />
                    )}
                    freeSolo
                    selectOnFocus
                    clearOnBlur={false}
                    handleHomeEndKeys
                    disableClearable
                  />
                  <TextField
                    variant="outlined"
                    label="Policy Number"
                    name="policyNumber"
                    value={formData.policyNumber}
                    onChange={handleChange}
                    error={!!errors.policyNumber}
                    helperText={errors.policyNumber}
                    inputProps={{
                      maxLength: 50,
                      autoCapitalize: "characters"
                    }}
                    sx={textFieldStyles}
                  />
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: isTinyScreen ? '12px' : '20px', mb: isTinyScreen ? '16px' : '24px' }}>
                  <Autocomplete
                    options={availablePolicyTypes}
                    value={formData.policyType}
                    onChange={(event, newValue) => {
                      setFormData(prev => ({ ...prev, policyType: newValue || "" }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Policy Type"
                        variant="outlined"
                        sx={autocompleteStyles}
                        error={!!errors.policyType}
                        helperText={errors.policyType}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <KeyboardArrowDownIcon
                              sx={{
                                color: '#757575',
                                position: 'absolute',
                                right: '12px',
                                pointerEvents: 'none'
                              }}
                            />
                          ),
                        }}
                      />
                    )}
                    freeSolo
                    selectOnFocus
                    clearOnBlur={false}
                    handleHomeEndKeys
                    disableClearable
                  />
                  <TextField
                    variant="outlined"
                    label="Expiry Date"
                    name="expiryDate"
                    value={formData.expiryDate}
                    onChange={handleChange}
                    error={!!errors.expiryDate}
                    helperText={errors.expiryDate}
                    placeholder="DD-MM-YYYY"
                    inputProps={{
                      maxLength: 10,
                      inputMode: "numeric"
                    }}
                    sx={textFieldStyles}
                  />
                </Box>
                {category !== "motor" && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: isTinyScreen ? '12px' : '20px', mb: isTinyScreen ? '16px' : '24px' }}>
                    <TextField
                      variant="outlined"
                      label="Sum Insured"
                      name="sumInsured"
                      value={formData.sumInsured}
                      onChange={handleChange}
                      inputProps={{
                        maxLength: 15,
                        inputMode: "decimal"
                      }}
                      sx={textFieldStyles}
                    />
                    <Box />
                  </Box>
                )}

                <h3
                  style={{
                    fontFamily: 'Roboto Slab',
                    fontWeight: 500,
                    fontSize: isTinyScreen ? '16px' : '20px',
                    lineHeight: isTinyScreen ? '24px' : '32px',
                    letterSpacing: '1%',
                    color: '#212121',
                    marginBottom: isTinyScreen ? '12px' : '16px'
                  }}
                >
                  Payment
                </h3>
                {category === "motor" && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: isTinyScreen ? '12px' : '20px', mb: isTinyScreen ? '16px' : '24px' }}>
                    {formData.policyType !== "Liability" && (
                      <TextField
                        variant="outlined"
                        label="OD Premium"
                        name="odPremium"
                        value={formData.odPremium}
                        onChange={handleChange}
                        inputProps={{
                          inputMode: "decimal"
                        }}
                        sx={textFieldStyles}
                      />
                    )}
                    {(formData.policyType === "Package" || formData.policyType === "Liability") && (
                      <TextField
                        variant="outlined"
                        label="TP Premium"
                        name="tpPremium"
                        value={formData.tpPremium}
                        onChange={handleChange}
                        inputProps={{
                          inputMode: "decimal"
                        }}
                        sx={textFieldStyles}
                      />
                    )}
                    <TextField
                      variant="outlined"
                      label="Net Premium"
                      name="netPremium"
                      value={formData.netPremium}
                      onChange={handleChange}
                      error={!!errors.netPremium}
                      helperText={errors.netPremium}
                      inputProps={{
                        inputMode: "decimal"
                      }}
                      sx={textFieldStyles}
                    />
                    <TextField
                      variant="outlined"
                      label="Total Premium"
                      name="premiumAmount"
                      value={formData.premiumAmount}
                      onChange={handleChange}
                      error={!!errors.premiumAmount}
                      helperText={errors.premiumAmount}
                      inputProps={{
                        inputMode: "decimal"
                      }}
                      sx={textFieldStyles}
                    />
                    <TextField
                      variant="outlined"
                      label="Customer Discount"
                      name="discount"
                      value={formData.discount}
                      onChange={handleChange}
                      error={!!errors.discount}
                      helperText={errors.discount}
                      inputProps={{
                        inputMode: "decimal"
                      }}
                      sx={textFieldStyles}
                    />
                    <TextField
                      variant="outlined"
                      label="Amount Payable"
                      name="amountPayable"
                      value={formData.amountPayable}
                      onChange={handleChange}
                      error={!!errors.amountPayable}
                      helperText={errors.amountPayable}
                      inputProps={{
                        inputMode: "decimal",
                        readOnly: true
                      }}
                      sx={textFieldStyles}
                    />
                    <TextField
                      variant="outlined"
                      label="Amount Received"
                      name="paidAmount"
                      value={formData.paidAmount}
                      onChange={handleChange}
                      error={!!errors.paidAmount}
                      helperText={errors.paidAmount}
                      inputProps={{
                        inputMode: "decimal"
                      }}
                      sx={textFieldStyles}
                    />
                    <Autocomplete
                      options={paymentModes}
                      value={formData.modeOfPayment}
                      onChange={(event, newValue) => {
                        setFormData(prev => ({ ...prev, modeOfPayment: newValue || "" }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Mode of Payment"
                          variant="outlined"
                          sx={autocompleteStyles}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <KeyboardArrowDownIcon
                                sx={{
                                  color: '#757575',
                                  position: 'absolute',
                                  right: '12px',
                                  pointerEvents: 'none'
                                }}
                              />
                            ),
                          }}
                        />
                      )}
                      freeSolo
                      selectOnFocus
                      clearOnBlur={false}
                      handleHomeEndKeys
                      disableClearable
                    />
                    <TextField
                      variant="outlined"
                      label="Remarks"
                      name="remarks"
                      value={formData.remarks}
                      onChange={handleChange}
                      multiline
                      rows={2}
                      inputProps={{
                        maxLength: 200,
                        autoCapitalize: "sentences"
                      }}
                      sx={{
                        ...textFieldStyles,
                        "& .MuiInputBase-root": {
                          ...textFieldStyles["& .MuiInputBase-root"],
                          height: "auto",
                          minHeight: "56px"
                        }
                      }}
                    />
                  </Box>
                )}
                {category !== "motor" && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: isTinyScreen ? '12px' : '20px', mb: isTinyScreen ? '16px' : '24px' }}>
                    <TextField
                      variant="outlined"
                      label="Net Premium"
                      name="netPremium"
                      value={formData.netPremium}
                      onChange={handleChange}
                      error={!!errors.netPremium}
                      helperText={errors.netPremium}
                      inputProps={{
                        inputMode: "decimal"
                      }}
                      sx={textFieldStyles}
                    />
                    <TextField
                      variant="outlined"
                      label="Total Premium"
                      name="premiumAmount"
                      value={formData.premiumAmount}
                      onChange={handleChange}
                      error={!!errors.premiumAmount}
                      helperText={errors.premiumAmount}
                      inputProps={{
                        inputMode: "decimal"
                      }}
                      sx={textFieldStyles}
                    />
                    <TextField
                      variant="outlined"
                      label="Customer Discount"
                      name="discount"
                      value={formData.discount}
                      onChange={handleChange}
                      error={!!errors.discount}
                      helperText={errors.discount}
                      inputProps={{
                        inputMode: "decimal"
                      }}
                      sx={textFieldStyles}
                    />
                    <TextField
                      variant="outlined"
                      label="Amount Payable"
                      name="amountPayable"
                      value={formData.amountPayable}
                      onChange={handleChange}
                      error={!!errors.amountPayable}
                      helperText={errors.amountPayable}
                      inputProps={{
                        inputMode: "decimal",
                        readOnly: true
                      }}
                      sx={textFieldStyles}
                    />
                    <TextField
                      variant="outlined"
                      label="Amount Received"
                      name="paidAmount"
                      value={formData.paidAmount}
                      onChange={handleChange}
                      error={!!errors.paidAmount}
                      helperText={errors.paidAmount}
                      inputProps={{
                        inputMode: "decimal"
                      }}
                      sx={textFieldStyles}
                    />
                    <Autocomplete
                      options={paymentModes}
                      value={formData.modeOfPayment}
                      onChange={(event, newValue) => {
                        setFormData(prev => ({ ...prev, modeOfPayment: newValue || "" }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Mode of Payment"
                          variant="outlined"
                          sx={autocompleteStyles}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <KeyboardArrowDownIcon
                                sx={{
                                  color: '#757575',
                                  position: 'absolute',
                                  right: '12px',
                                  pointerEvents: 'none'
                                }}
                              />
                            ),
                          }}
                        />
                      )}
                      freeSolo
                      selectOnFocus
                      clearOnBlur={false}
                      handleHomeEndKeys
                      disableClearable
                    />
                    <TextField
                      variant="outlined"
                      label="Remarks"
                      name="remarks"
                      value={formData.remarks}
                      onChange={handleChange}
                      multiline
                      rows={2}
                      inputProps={{
                        maxLength: 200,
                        autoCapitalize: "sentences"
                      }}
                      sx={{
                        ...textFieldStyles,
                        "& .MuiInputBase-root": {
                          ...textFieldStyles["& .MuiInputBase-root"],
                          height: "auto",
                          minHeight: "56px"
                        }
                      }}
                    />
                  </Box>
                )}
              </form>
            </div>

            {/* Footer */}
            <Box
              sx={{
                padding: "16px 24px",
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                flexShrink: 0,
              }}
            >
              {/* Cancel Button */}
              <Button
                onClick={onClose}
                variant="outlined"
                sx={{
                  width: "149px",
                  height: "60px",
                  borderRadius: "8px",
                  borderColor: "#B00020",
                  color: "#B00020",
                  fontFamily: "Roboto",
                  fontWeight: 400,
                  fontSize: "20px",
                  lineHeight: "24px",
                  letterSpacing: "0.01em",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "inherit",
                    color: "#B00020",
                    boxShadow: 10
                  }
                }}
              >
                Cancel
              </Button>

              {/* Submit Button */}
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isSubmitting}
                sx={{
                  width: "149px",
                  height: "60px",
                  px: "12px",
                  py: "16px",
                  borderRadius: "8px",
                  backgroundColor: isSubmitting ? "#7e9ed8" : "#0063F7 !important",
                  color: "#FFFFFF",
                  fontFamily: "Roboto",
                  fontWeight: 400,
                  fontSize: "18px",
                  lineHeight: "22px",
                  letterSpacing: "0.5px",
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  position: "relative",
                  transition: "all 0.3s ease",
                  "&:hover:not(:disabled)": {
                    backgroundColor: "#0051d4 !important",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
                  },
                  "&:active:not(:disabled)": {
                    transform: "translateY(0)"
                  },
                  "&:disabled": {
                    cursor: "not-allowed",
                    backgroundColor: "#7e9ed8 !important"
                  }
                }}
              >
                {isSubmitting ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} thickness={4} sx={{ color: '#fff' }} />
                    Saving...
                  </Box>
                ) : (
                  "Save Policy"
                )}
              </Button>
            </Box>
          </div>
        </div>
      )}
      {showValidationBanner && (
        <Box
          sx={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10000,
            backgroundColor: '#FFADA8',
            color: '#F04438',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 500,
            padding: '12px 16px',
            minHeight: '44px',
            alignItems: 'center',
            gap: '2px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            display: 'inline-flex',
            maxWidth: '90vw',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ color: '#F04438', marginRight: '8px' }}
          >
            <path
              d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Please fill all mandatory fields
        </Box>
      )}

      {
        showSuccess && (
          <SuccessDialog
            isOpen={true}
            pdfUrl={finalPdfUrl}
            headerText={mode === "update" ? "Renewal Success" : "Policy Added Successfully"}
            messageText={mode === "update" ? "Policy renewal completed. Share with customer via WhatsApp." : "Policy has been successfully added. Share with customer via WhatsApp."}
            agentName={user?.displayName || "Your Agent"}
            premiumAmount={formData.amountPayable}
            balanceAmount={Math.max((Number(formData.amountPayable) || 0) - (Number(formData.paidAmount) || 0), 0)}
            onClose={() => {
              setShowSuccess(false);
            }}
          />
        )
      }
    </>
  );
};

export default UnifiedPolicyForm;
